import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { ProfileMenuComponent } from '@sbe-spinoff-client/app-shell';
import { SpinoffAuthService } from '../../auth/spinoff-auth.service';

/**
 * 계정 관련 드롭다운 버튼("프로필 메뉴") — 툴바 우측(맨 끝)에 배치한다.
 *
 * `SpinoffOrgContextButtonComponent`를 컨텍스트 스위처(`SpinoffOrgSwitcherComponent`, 툴바
 * 좌측)와 이 컴포넌트로 분리한 것 — GitHub 스타일 좌우 배치. 단체 전환·목록은 여기 없다.
 *
 * 실제 렌더링(아바타 트리거·메뉴·로그아웃 버튼)은 `@sbe-spinoff-client/app-shell`의
 * `ProfileMenuComponent`(인증 서비스에 의존하지 않는 순수 프레젠테이션 컴포넌트)에 위임한다
 * — 이 컴포넌트는 `SpinoffAuthService`에서 `currentUser`만 뽑아 넘기는 얇은 어댑터다
 * (2026-08-12, 로그인 가능한 모든 앱이 같은 드롭다운을 쓰도록 통일하며 분리). 외부 API
 * (selector·`logoutRequested`·`[profileMenuSection]` 슬롯)는 그대로 유지해 기존 소비 앱
 * (sbe-commerce/user-web, sbe-sms/user-web)은 변경 없이 그대로 동작한다.
 *
 * - `logoutRequested`: 로그아웃 클릭 시 방출.
 * - `[profileMenuSection]` 투영 슬롯 — 계정에 속한 화면 전용 메뉴 항목(예: sbe-commerce의
 *   "주문 내역", "내 스토어")을 여기 넣는다. 아무것도 투영하지 않으면 렌더링되지 않는다.
 *   투영하는 쪽에서 맨 앞에 `<mat-divider />`를 넣어 구분선을 제어한다.
 * - `loading`: 로그인 판정이 아직 끝나지 않은 상태(`ProfileMenuComponent.loading` 그대로
 *   전달) — 헤더가 로그인/프로필 판정 전에 이 컴포넌트를 반투명 비활성 아바타로 먼저
 *   보여줄 때 쓴다(app.html의 "로딩 → 인증됨/미인증" 3-상태 분기 참고).
 *
 * 컴포넌트 자체는 라우터에 의존하지 않는다. 각 앱은 이벤트를 받아 자체 네비게이션 로직을
 * 실행한다.
 */
@Component({
  selector: 'spinoff-profile-menu',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ProfileMenuComponent],
  template: `
    <sbe-profile-menu [user]="currentUser()" [loading]="loading()" (logoutRequested)="logoutRequested.emit()">
      <ng-content select="[profileMenuSection]" />
    </sbe-profile-menu>
  `,
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
export class SpinoffProfileMenuComponent {
  private readonly auth = inject(SpinoffAuthService);

  readonly loading = input(false);
  readonly logoutRequested = output<void>();

  protected readonly currentUser = this.auth.currentUser;
}
